import React from 'react';
import { getIntl } from '../../../utils/intl';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';
import useProfileTransferRequest from '../../../hooks/api/useProfileTransferRequest';
import useInboundTransferRequests from '../../../hooks/api/useInboundTransferRequests';
import { useIsProfileOwner } from '../Permissions/hooks';
import Alert from '../Alert';
import getName from '../../../utils/getName';

const intl = getIntl('TransferAlertLogCard', {
    observerTitle: 'Profile Ownership Transfer In-Progress',
    observerCollaboratorTransferMessage: `Profile administration is in the process of being transferred from {fromUserFullName} to the collaborator {toUserFullName}.`,
    observerNonCollaboratorTransferMessage: `Profile administration is in the process of being transferred from {fromUserFullName} to user with the email "{toUserEmail}".`,
    recipientTitle: 'Profile Ownership Transfer Request',
    recipientMessage: `You have been requested to take over profile administration from {fromUserFullName}.`,
    senderTitle: 'Active Ownership Transfer Request',
    senderCollaboratorMessage: `You have requested to transfer profile administration to {toUserFullName}.`,
    senderNonCollaboratorMessage: `You have requested to transfer profile administration to user with the email "{toUserEmail}".`,
    manageRequest: 'Manage Transfer Request',
    respondToRequest: 'Respond to Transfer Request',
});

export type PendingTransferRequestAlertOwnProps = {
    profileId: string;
};

export type PendingTransferRequestAlertProps = PendingTransferRequestAlertOwnProps;

const PendingTransferRequestAlert: React.FC<PendingTransferRequestAlertProps> = ({ profileId }) => {
    const navigation = useProfilesTabNavigation();
    const { isLoadingProfileTransferRequest, profileTransferRequest } = useProfileTransferRequest({
        profileId,
    });
    const { isLoadingInboundTransferRequests, inboundTransferRequests } = useInboundTransferRequests();
    const isProfileOwner = useIsProfileOwner(profileId);

    if (
        isLoadingProfileTransferRequest ||
        isLoadingInboundTransferRequests ||
        profileTransferRequest.status === 'no-pending-transfer-request'
    )
        return null;

    const userRecipientTransferRequest = inboundTransferRequests.find(
        (request) => request.profile.medicalProfileId === profileId,
    );

    if (isProfileOwner) {
        return (
            <Alert
                style={{ marginBottom: 8 }}
                type="info"
                title={intl('senderTitle')}
                message={
                    profileTransferRequest.status === 'pending-collaborator-transfer-request'
                        ? intl('senderCollaboratorMessage', {
                              toUserFullName: getName(profileTransferRequest.transferToUser),
                          })
                        : intl('senderNonCollaboratorMessage', {
                              toUserEmail: profileTransferRequest.transferToEmail,
                          })
                }
                actionItems={[
                    {
                        title: intl('manageRequest'),
                        onPress: () =>
                            navigation.navigate('transfer-request-response', {
                                transferProfileId: profileId,
                                transferRequestId: profileTransferRequest.medicalProfileOwnershipTransferId,
                            }),
                        testID: 'transfer-request-alert-button',
                    },
                ]}
                testID="transfer-request-alert"
            />
        );
    } else if (userRecipientTransferRequest) {
        return (
            <Alert
                style={{ marginBottom: 8 }}
                type="info"
                title={intl('recipientTitle')}
                message={intl('recipientMessage', {
                    fromUserFullName: getName(profileTransferRequest.transferFromUser),
                })}
                actionItems={[
                    {
                        title: intl('manageRequest'),
                        onPress: () =>
                            navigation.navigate('transfer-request-response', {
                                transferProfileId: profileId,
                                transferRequestId: userRecipientTransferRequest.medicalProfileOwnershipTransferId,
                            }),
                        testID: 'transfer-request-alert-button',
                    },
                ]}
                testID="transfer-request-alert"
            />
        );
    } else {
        return (
            <Alert
                style={{ marginBottom: 8 }}
                type="info"
                title={intl('observerTitle')}
                message={
                    profileTransferRequest.status === 'pending-collaborator-transfer-request'
                        ? intl('observerCollaboratorTransferMessage', {
                              fromUserFullName: getName(profileTransferRequest.transferFromUser),
                              toUserFullName: getName(profileTransferRequest.transferToUser),
                          })
                        : intl('observerNonCollaboratorTransferMessage', {
                              fromUserFullName: getName(profileTransferRequest.transferFromUser),
                              toUserEmail: profileTransferRequest.transferToEmail,
                          })
                }
                testID="transfer-request-alert"
            />
        );
    }
};

export default React.memo(PendingTransferRequestAlert);
